/**
 * PorterPanel — the editor's right column. Porter is the AI assistant; this is
 * the real chat UI (replacing the earlier coming-soon placeholder), wired to the
 * server-side Claude proxy at POST /api/porter/chat.
 *
 * Architecture (see .context/api-contract-porter.md — read the "Statelessness"
 * section): Porter NEVER mutates the map on its own. Every reply that carries a
 * `results` block renders a table plus three explicit buttons — Add to map /
 * Save as dataset / CSV — that the human clicks. The server keeps no
 * conversation/ref state, so each result row already carries everything an action
 * needs (geoid, name, population, distance_mi, state); we resend recent plain-text
 * turns as `history` on each request.
 *
 * What this component owns:
 *   - message history (user bubbles right/blue, AI bubbles left/sunken),
 *   - the input row + suggestion chips (chips prefill the input),
 *   - the send flow: append the user turn, POST, append the reply, render any
 *     results table,
 *   - the three result-actions:
 *       • Add to map  → builds a new group from the rows (one area per row,
 *         matching GroupCard's area shape) and hands it up to the Editor, which
 *         appends it to the map document; the normal debounced autosave persists
 *         it and the server resolves real geometry on save (same as any manually
 *         typed area). No separate save call here.
 *       • Save as dataset → POST /api/datasets with the structured source_query
 *         the backend threaded into the response (so the dataset is refreshable).
 *       • CSV → client-side download of just that result's rows, formula-injection
 *         hardened the same way the server's csvSafe() is.
 *
 * The map document, its groups, and autosave live in Editor.js; this component
 * only calls back up. It holds transient chat state (messages, input, loading).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Send, Loader2, MapPinPlus, Database, Download, Target } from 'lucide-react';
import api, { ApiError } from '../utils/api';
import { useToast } from '../context/ToastContext';

// Quick-action chips from the mock-up. Clicking one prefills the input (it does
// NOT auto-send — the user still reviews/edits before sending).
const SUGGESTIONS = [
  'ZIPs in drawn area',
  'Neighborhoods here',
  'Population here',
  'Ad targeting tiers',
];

// How many prior plain-text turns to resend as history. The server is stateless
// (F-1/F-6): no tool_use/tool_result blocks ever leave it, so the client replays
// recent user/assistant text to give Claude context. Cap it to keep the payload
// and token cost bounded on long conversations.
const HISTORY_TURNS = 12;

// After this long with no reply, swap the generic "thinking" copy for one
// that says large queries can genuinely take a while — a state-wide area
// query (e.g. "all zips in Ohio") can take 20-30s server-side, and the
// static spinner otherwise looks identical whether a turn takes 2s or 30s.
const SLOW_RESPONSE_MS = 8000;

// results.kind → singular area_type used in group_areas objects. Mirrors the
// contract: cities→city, counties→county, zips→zip, tracts→tract.
const KIND_TO_AREA_TYPE = {
  cities: 'city',
  counties: 'county',
  zips: 'zip',
  tracts: 'tract',
};

// Characters that make a spreadsheet treat a CSV cell as a formula. Mirrors the
// server's csvHelpers.FORMULA_TRIGGERS so the client-side "CSV" export is
// hardened the same way as the server exports (security review requirement).
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];

// Client-side id for a new group/area (server assigns final ids on save). Matches
// mintId()/tempId() in Editor.js and GroupCard.js.
function mintId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// Make one value safe for a CSV cell: formula-injection guard (prefix a leading
// trigger char with a single quote) + RFC-4180 quoting. Behavior-mirrors the
// server's csvSafe() so a client-exported CSV is escaped identically.
function csvSafe(value) {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (s.length && FORMULA_TRIGGERS.includes(s[0])) {
    s = `'${s}`;
  }
  if (/[",\n\r]/.test(s)) {
    s = `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Slugify a title into a safe download filename stem (mirrors server slugify()).
function slugify(name) {
  return String(name || 'porter-results')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'porter-results';
}

// Human label for one result row in the results table. Cities/counties get a
// ", ST" suffix when a state is present; ZIPs/tracts show the geoid as the name.
function rowLabel(row) {
  const name = row.name || row.geoid || '';
  return row.state ? `${name}, ${row.state}` : name;
}

// Build the group_areas array for a results block — one area per row, in the
// exact shape GroupCard.areaFromResolved() produces for manual entry, so the map
// document is consistent no matter where an area came from. Census-backed areas
// carry geoid + empty geometry (server resolves geometry from geoid on save).
function areasFromResults(results) {
  const areaType = KIND_TO_AREA_TYPE[results.kind] || 'zip';
  return (results.rows || []).map((row, i) => {
    const baseName = row.name || row.geoid || '';
    // County labels carry ", ST" (matches GroupCard chip rendering); others are
    // name-only, falling back to the geoid.
    const label =
      areaType === 'county' && row.state ? `${baseName}, ${row.state}` : baseName || row.geoid || '';
    return {
      id: mintId(),
      area_type: areaType,
      label,
      geoid: row.geoid || null,
      boundary_id: null,
      geometry: {},
      params: {},
      sort_order: i,
      // Kept for chip rendering (harmless if not persisted), same as GroupCard.
      _state: row.state || null,
    };
  });
}

/**
 * @param {object} props
 * @param {(group: object) => void} props.onAddGroup - append a fully-built group
 *   (with areas) to the map document; Editor persists it via autosave.
 * @param {string|null} [props.mapClientId] - the map's client_id, sent as the
 *   dataset's client_id on "Save as dataset" (null → an unassigned dataset).
 * @param {number} [props.nextGroupIndex] - current group count, used to pick the
 *   new group's palette color so Porter groups match manually-added ones.
 * @param {object|null} [props.drawnArea] - GeoJSON geometry of "this area" for
 *   get_drawn_area / use_drawn_area — whatever's currently selected on the
 *   map (Editor's porterAreaContext), or the last freeform shape drawn if
 *   nothing is selected. Sent as `drawn_area`.
 * @param {string|null} [props.areaLabel] - human-readable name for drawnArea
 *   (e.g. a group's name), shown above the input so the user can SEE which
 *   area Porter will use — display only, never sent to the server.
 */
export default function PorterPanel({
  onAddGroup,
  mapClientId = null,
  nextGroupIndex = 0,
  drawnArea = null,
  areaLabel = null,
}) {
  const { showToast } = useToast();

  // Chat transcript. Each message: { id, role: 'user'|'assistant', text,
  // results? }. `results` (assistant only) drives the table + action buttons.
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Flips true if `sending` is still true after SLOW_RESPONSE_MS — see the
  // constant's comment. Reset whenever a new turn starts or the current one
  // finishes, so it never carries over to the next (fast) turn.
  const [sendingSlow, setSendingSlow] = useState(false);
  // Track which results block is being turned into a dataset (spinner + disable),
  // keyed by message id, so only the clicked button shows progress.
  const [savingDatasetId, setSavingDatasetId] = useState(null);

  useEffect(() => {
    if (!sending) {
      setSendingSlow(false);
      return undefined;
    }
    const t = window.setTimeout(() => setSendingSlow(true), SLOW_RESPONSE_MS);
    return () => window.clearTimeout(t);
  }, [sending]);

  // Palette shared with Editor's GROUP_PALETTE so Porter-created groups get the
  // same next color a manual "Add group" would.
  const GROUP_PALETTE = useMemo(
    () => ['#2563EB', '#DC2626', '#16A34A', '#9333EA', '#EA580C', '#0891B2', '#CA8A04'],
    []
  );

  const msgListRef = useRef(null);

  // Scroll the transcript to the newest message after each change.
  const scrollToBottom = useCallback(() => {
    const el = msgListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Send the current input as a turn. Optimistically appends the user bubble,
  // POSTs with recent history (+ drawn_area when present), then appends the
  // assistant reply (with any results block).
  const send = useCallback(
    async (text) => {
      const message = text.trim();
      if (!message || sending) return;

      const userMsg = { id: mintId(), role: 'user', text: message };
      // Build history from the transcript BEFORE this turn — plain text only,
      // capped, matching the request contract's { role, text } shape. Error
      // bubbles (failed/rate-limited turns) are dropped so Porter doesn't treat
      // its own apology text as a real prior turn.
      const priorHistory = messages
        .filter((m) => !m.isError && m.text)
        .slice(-HISTORY_TURNS)
        .map((m) => ({ role: m.role, text: m.text }));

      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setSending(true);
      // Defer scroll so the new bubble is in the DOM first.
      window.setTimeout(scrollToBottom, 0);

      try {
        const body = { message, history: priorHistory };
        // Only include drawn_area when the user actually has a shape on the map —
        // the contract makes it optional and get_drawn_area needs the geometry.
        if (drawnArea && drawnArea.type) body.drawn_area = drawnArea;

        const res = await api.post('/porter/chat', body);
        const assistantMsg = {
          id: mintId(),
          role: 'assistant',
          text: res.reply || '',
          // `results` is present only when the turn produced rows; keep the raw
          // block so the actions can read kind/title/rows/source_query directly.
          results: res.results || null,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      } catch (err) {
        // 429 = rate limited: friendly "slow down" rather than a raw error. Other
        // failures get a generic apology bubble so the chat stays coherent.
        const isRateLimited = err instanceof ApiError && err.status === 429;
        const errorText = isRateLimited
          ? 'You’re sending messages a little fast — give it a moment and try again.'
          : (err && err.message) ||
            'Porter couldn’t answer that just now. Please try again in a moment.';
        setMessages((prev) => [
          ...prev,
          { id: mintId(), role: 'assistant', text: errorText, isError: true },
        ]);
        if (!isRateLimited) {
          showToast('Porter request failed.', { variant: 'error' });
        }
      } finally {
        setSending(false);
        window.setTimeout(scrollToBottom, 0);
      }
    },
    [messages, sending, drawnArea, scrollToBottom, showToast]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  // ─── Result actions ───────────────────────────────────────────────────────

  // Add to map: build a new group from the results and hand it to the Editor,
  // which appends it to the document. Autosave (Editor) persists it and the
  // server resolves geometry on save — no direct save call from here.
  const handleAddToMap = useCallback(
    (results) => {
      const areas = areasFromResults(results);
      if (areas.length === 0) {
        showToast('Nothing to add — that result has no rows.', { variant: 'error' });
        return;
      }
      const color = GROUP_PALETTE[nextGroupIndex % GROUP_PALETTE.length];
      const group = {
        id: mintId(),
        // The group takes the result's title so the user recognizes it on the map.
        name: results.title || 'Porter results',
        color,
        fill_opacity: 0.45,
        show_labels: false,
        merge_shapes: false,
        is_visible: true,
        source_dataset_id: null,
        sort_order: nextGroupIndex,
        areas,
      };
      onAddGroup(group);
      showToast(`Added ${areas.length} ${areas.length === 1 ? 'area' : 'areas'} to the map`);
    },
    [GROUP_PALETTE, nextGroupIndex, onAddGroup, showToast]
  );

  // Save as dataset: POST /api/datasets with the structured source_query the
  // backend threaded into the response, so the dataset is refreshable later.
  const handleSaveDataset = useCallback(
    async (msgId, results) => {
      if (savingDatasetId) return;
      setSavingDatasetId(msgId);
      // Everything that can throw lives inside this try — including the spec/
      // row derivation below, which used to run BEFORE the try. That call site
      // (onSaveDataset={() => handleSaveDataset(...)}) never awaits or catches
      // the promise this returns, so a throw out there became an unhandled
      // promise rejection: no toast, no state reset, the button just looked
      // like it did nothing. Bringing it inside the try guarantees any failure
      // — a malformed row, a spec that won't stringify, the network call
      // itself — always surfaces as a toast and always clears the spinner.
      try {
        // The backend threads the structured query spec through so the client
        // can save a refreshable dataset without re-deriving it. Accept the
        // couple of field names it might land on (contract said "check the
        // exact field name") — source_query is the contract name;
        // source_spec/query_spec are tolerated.
        const spec = results.source_query ?? results.source_spec ?? results.query_spec ?? null;
        if (spec == null) {
          // Without the structured spec the dataset couldn't be refreshed (the
          // refresh endpoint 422s on a non-spec source_query). Don't silently
          // save an un-refreshable dataset — tell the user why.
          showToast(
            'Can’t save this as a dataset — Porter didn’t return a refreshable query for it.',
            { variant: 'error' }
          );
          return;
        }

        // The datasets contract wants source_query as a JSON string (the
        // refresh endpoint does JSON.parse on it). Stringify an object spec;
        // pass a string spec through untouched.
        const sourceQuery = typeof spec === 'string' ? spec : JSON.stringify(spec);

        // dataset_rows only carry geoid/name/population/distance_mi — send
        // exactly those (drop the state/extra fields the table used for
        // display).
        const rows = (results.rows || []).map((r) => ({
          geoid: r.geoid ?? null,
          name: r.name ?? null,
          population: r.population ?? null,
          distance_mi: r.distance_mi ?? null,
        }));

        const payload = {
          name: results.title || 'Porter dataset',
          dataset_type: results.kind,
          source_query: sourceQuery,
          rows,
        };
        // Only attach client_id when the map has one; an unassigned map saves an
        // unassigned dataset (the column is nullable).
        if (mapClientId) payload.client_id = mapClientId;

        await api.post('/datasets', payload);
        showToast('Saved as dataset');
      } catch (err) {
        showToast((err && err.message) || 'Could not save the dataset.', { variant: 'error' });
      } finally {
        setSavingDatasetId(null);
      }
    },
    [savingDatasetId, mapClientId, showToast]
  );

  // CSV: serialize just this result's rows client-side and trigger a download.
  // Column set mirrors the dataset CSV: GEOID, name, population, distance (when
  // the rows carry it). Every cell goes through csvSafe() (formula-injection +
  // RFC-4180), matching the server exports.
  const handleCsv = useCallback(
    (results) => {
      const rows = results.rows || [];
      if (rows.length === 0) {
        showToast('Nothing to export — that result has no rows.', { variant: 'error' });
        return;
      }
      // Include the distance column only when at least one row has a distance
      // (radius queries do; county/drawn-area ones don't).
      const hasDistance = rows.some((r) => r.distance_mi != null);
      const header = ['geoid', 'name', 'state', 'population'];
      if (hasDistance) header.push('distance_mi');

      const lines = [header.join(',')];
      for (const r of rows) {
        const cells = [
          csvSafe(r.geoid),
          csvSafe(r.name),
          csvSafe(r.state),
          csvSafe(r.population),
        ];
        if (hasDistance) cells.push(csvSafe(r.distance_mi));
        lines.push(cells.join(','));
      }
      // CRLF line endings match RFC-4180 / the server exports.
      const csv = lines.join('\r\n');

      // Download via a transient blob URL (client-side, no endpoint needed).
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slugify(results.title)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast(`Exported ${rows.length} ${rows.length === 1 ? 'row' : 'rows'} to CSV`);
    },
    [showToast]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <aside className="porter-panel" aria-label="Porter assistant">
      <div className="porter-head">
        <div className="porter-avatar">
          <Sparkles size={15} />
        </div>
        <div>
          <div className="porter-title">Porter</div>
          <div className="porter-sub">Ask the map — data, areas &amp; targeting</div>
        </div>
      </div>

      <div className="porter-msgs" ref={msgListRef}>
        {messages.length === 0 && (
          <div className="pmsg ai porter-placeholder">
            Ask about the areas on this map — populations, top cities,
            neighborhoods, or ad-targeting tiers. When Porter returns a list you
            can add it straight to the map, save it as a dataset, or download a CSV.
          </div>
        )}

        {messages.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="pmsg user">
              {m.text}
            </div>
          ) : (
            <div key={m.id} className={`pmsg ai${m.isError ? ' pmsg-error' : ''}`}>
              {m.text && <div className="pmsg-text">{m.text}</div>}
              {m.results && (
                <ResultsBlock
                  results={m.results}
                  onAddToMap={() => handleAddToMap(m.results)}
                  onSaveDataset={() => handleSaveDataset(m.id, m.results)}
                  onCsv={() => handleCsv(m.results)}
                  savingDataset={savingDatasetId === m.id}
                />
              )}
            </div>
          )
        )}

        {sending && (
          <div className="pmsg ai porter-typing" aria-live="polite">
            <Loader2 size={13} className="spin" />
            {sendingSlow
              ? 'Still working — large area queries (like a whole state) can take up to 30 seconds…'
              : 'Porter is thinking…'}
          </div>
        )}
      </div>

      <div className="porter-chips">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            className="filter-chip"
            onClick={() => setInput(s)}
            disabled={sending}
          >
            {s}
          </button>
        ))}
      </div>

      {/* "This area" indicator — the click-to-select-driven context
          (Editor's porterAreaContext) has no other visible feedback anywhere
          on screen, which is exactly what made it hard to tell whether/which
          area a query would use once a map had more than one shape. Always
          shown (not just when something's available) so the empty state is
          equally clear: if you ask about "this area" with nothing selected
          or drawn, this is why it won't work yet. */}
      <div className="porter-area-context">
        {areaLabel ? (
          <>
            <Target size={12} />
            <span>
              Using <strong>{areaLabel}</strong> as “this area”
            </span>
          </>
        ) : (
          <span className="porter-area-context-empty">
            No area selected — click a shape on the map, or draw one, to ask about “this area”
          </span>
        )}
      </div>

      <form className="porter-input-row" onSubmit={handleSubmit}>
        <input
          className="input"
          type="text"
          placeholder="Ask Porter anything about this map…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          aria-label="Message Porter"
        />
        <button
          className="btn btn-primary btn-sm"
          type="submit"
          disabled={sending || !input.trim()}
          aria-label="Send"
        >
          {sending ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
        </button>
      </form>
    </aside>
  );
}

/**
 * ResultsBlock — the compact table + three action buttons rendered inside an AI
 * message when the turn returned a `results` block. Column set adapts to the
 * data: a "Distance" column appears only for radius results (rows with
 * distance_mi); population is right-aligned like the mock-up.
 *
 * @param {object} props
 * @param {{kind, title, truncated, rows}} props.results
 * @param {() => void} props.onAddToMap
 * @param {() => void} props.onSaveDataset
 * @param {() => void} props.onCsv
 * @param {boolean} props.savingDataset - true while the dataset POST is in flight
 */
function ResultsBlock({ results, onAddToMap, onSaveDataset, onCsv, savingDataset }) {
  const rows = results.rows || [];
  const hasDistance = rows.some((r) => r.distance_mi != null);

  return (
    <div className="porter-results">
      {results.title && <div className="porter-results-title">{results.title}</div>}

      <div className="table-wrap">
        <table className="table table-compact">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              {hasDistance && <th style={{ textAlign: 'right' }}>Dist. (mi)</th>}
              <th style={{ textAlign: 'right' }}>Pop. (2020)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.geoid || i}>
                <td>{i + 1}</td>
                <td>{rowLabel(row)}</td>
                {hasDistance && (
                  <td style={{ textAlign: 'right' }}>
                    {row.distance_mi != null ? row.distance_mi.toFixed(1) : '—'}
                  </td>
                )}
                <td style={{ textAlign: 'right' }}>
                  {row.population != null ? row.population.toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Truncation note — the contract's `truncated` flag means the server
          capped the row count; tell the user so they don't assume it's complete. */}
      {results.truncated && (
        <div className="porter-results-note">Showing a capped set — refine to narrow it.</div>
      )}

      <div className="actions">
        <button className="btn btn-primary btn-sm" onClick={onAddToMap}>
          <MapPinPlus size={13} /> Add to map
        </button>
        <button
          className="btn btn-outline btn-sm"
          onClick={onSaveDataset}
          disabled={savingDataset}
        >
          {savingDataset ? <Loader2 size={13} className="spin" /> : <Database size={13} />} Save as
          dataset
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onCsv}>
          <Download size={13} /> CSV
        </button>
      </div>
    </div>
  );
}
